import { makeAutoObservable, runInAction } from 'mobx';
import {
  storeSetupApi,
  type StoreSetupStep,
  type StoreSetupSummary,
} from '@/api/store-setup';
import { RequestSequencer, isAbortError } from '@/api/requests';
import { normalizeApiError, type ApiError } from '@/lib/errors';

type StoreSetupStatus = 'idle' | 'loading' | 'success' | 'refreshing' | 'error';

interface StoreSetupApi {
  get: typeof storeSetupApi.get;
  setStep: typeof storeSetupApi.setStep;
  setHomeGuideVisible: typeof storeSetupApi.setHomeGuideVisible;
}

export class StoreSetupStore {
  summary: StoreSetupSummary | null = null;
  status: StoreSetupStatus = 'idle';
  error: ApiError | null = null;
  actionError: ApiError | null = null;
  savingStep: StoreSetupStep | null = null;
  savingHomeVisibility = false;
  scopeKey: string | null = null;

  private readonly api: StoreSetupApi;
  private readonly seq = new RequestSequencer();

  constructor(api?: Partial<StoreSetupApi>) {
    this.api = {
      get: api?.get ?? storeSetupApi.get,
      setStep: api?.setStep ?? storeSetupApi.setStep,
      setHomeGuideVisible: api?.setHomeGuideVisible ?? storeSetupApi.setHomeGuideVisible,
    };
    makeAutoObservable<StoreSetupStore, 'api' | 'seq'>(
      this,
      { api: false, seq: false },
      { autoBind: true },
    );
  }

  async load(organizationId: number | null, force = false): Promise<void> {
    if (organizationId === null) {
      this.reset();
      return;
    }

    const key = String(organizationId);
    if (key !== this.scopeKey) {
      this.reset();
      this.scopeKey = key;
    } else if (!force && (this.status === 'loading' || this.status === 'success')) {
      return;
    }

    const ticket = this.seq.begin('store-setup:load');
    this.status = this.summary ? 'refreshing' : 'loading';
    try {
      const summary = await this.api.get(ticket.signal);
      if (!ticket.isCurrent() || this.scopeKey !== key) return;
      runInAction(() => {
        this.summary = summary;
        this.status = 'success';
        this.error = null;
        this.actionError = null;
      });
    } catch (reason) {
      if (isAbortError(reason) || !ticket.isCurrent() || this.scopeKey !== key) return;
      runInAction(() => {
        this.status = 'error';
        this.error = normalizeApiError(reason);
      });
    }
  }

  async setStep(step: StoreSetupStep, completed: boolean): Promise<void> {
    if (!this.summary || this.isSaving) return;
    const key = this.scopeKey;
    this.seq.abort('store-setup:load');
    if (this.status === 'refreshing') this.status = 'success';
    this.savingStep = step;
    this.actionError = null;
    try {
      const summary = await this.api.setStep(step, completed);
      if (this.scopeKey !== key) return;
      runInAction(() => {
        this.summary = summary;
        this.status = 'success';
        this.error = null;
      });
    } catch (reason) {
      if (this.scopeKey !== key) return;
      runInAction(() => {
        this.actionError = normalizeApiError(reason);
      });
    } finally {
      if (this.scopeKey === key) {
        runInAction(() => {
          this.savingStep = null;
        });
      }
    }
  }

  async setHomeGuideVisible(visible: boolean): Promise<void> {
    if (!this.summary || this.isSaving) return;
    const key = this.scopeKey;
    this.seq.abort('store-setup:load');
    if (this.status === 'refreshing') this.status = 'success';
    this.savingHomeVisibility = true;
    this.actionError = null;
    try {
      const summary = await this.api.setHomeGuideVisible(visible);
      if (this.scopeKey !== key) return;
      runInAction(() => {
        this.summary = summary;
        this.status = 'success';
        this.error = null;
      });
    } catch (reason) {
      if (this.scopeKey !== key) return;
      runInAction(() => {
        this.actionError = normalizeApiError(reason);
      });
    } finally {
      if (this.scopeKey === key) {
        runInAction(() => {
          this.savingHomeVisibility = false;
        });
      }
    }
  }

  get isSaving(): boolean {
    return this.savingStep !== null || this.savingHomeVisibility;
  }

  isStepComplete(step: StoreSetupStep): boolean {
    return this.summary?.setup.completed_steps.includes(step) ?? false;
  }

  isStepReady(step: StoreSetupStep): boolean {
    const summary = this.summary;
    if (!summary) return false;

    switch (step) {
      case 'add_store_details':
        return summary.readiness.profile.has_logo && summary.readiness.profile.has_contact;
      case 'set_up_payments':
        return summary.readiness.payment_methods.active > 0;
      case 'set_up_delivery':
        return summary.readiness.delivery.enabled_methods > 0;
      case 'review_products':
        return true;
      case 'visit_store':
        return true;
    }
  }

  /**
   * Refreshes the server-computed readiness before completing a step. This is
   * used after a real settings write so the checklist never trusts stale client
   * state or marks an incomplete configuration as ready.
   */
  async completeStepWhenReady(
    organizationId: number | null,
    step: StoreSetupStep,
  ): Promise<boolean> {
    await this.load(organizationId, true);
    if (this.status !== 'success') {
      runInAction(() => {
        this.actionError = this.error;
      });
      return false;
    }
    if (!this.summary || !this.isStepReady(step)) return false;
    if (!this.isStepComplete(step)) await this.setStep(step, true);
    return this.isStepComplete(step);
  }

  reset(): void {
    this.seq.abortAll();
    this.summary = null;
    this.status = 'idle';
    this.error = null;
    this.actionError = null;
    this.savingStep = null;
    this.savingHomeVisibility = false;
    this.scopeKey = null;
  }
}
